import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';

import { api, ApiRequestError } from '../../lib/api.ts';
import { EMPTY_PLAN, PlanFields, planTermsToBody, type PlanTermsState } from './PlanFields.tsx';

/**
 * File a new affiliate.
 *
 * The design's rule is one sentence with three consequences: "a tracking link
 * and sub-ID prefix are issued on creation; commission starts on approval." So
 * this form collects the partner, its first link AND its commission terms, and
 * the endpoint writes all three in one transaction — an affiliate created with
 * no plan is one somebody would later approve into attributing players against
 * no commission basis.
 *
 * It starts PENDING. Nothing here approves anything, and the person filing it
 * is recorded so that they cannot be the one who does.
 */

/**
 * The traffic sources, matching `affiliateSourceKind` in @dayon/contracts.
 *
 * A closed list rather than a text box because `source_kind` is what the whole
 * screen filters, searches and groups on: free text makes "TikTok", "Tiktok"
 * and "tik tok" three different sources, and a quality comparison across them
 * compares nothing.
 */
const SOURCE_KINDS = [
  'SEO / review site',
  'SEO / blog',
  'Twitch / YouTube',
  'TikTok',
  'Facebook community',
  'Telegram channel',
  'Affiliate network',
  'Paid search',
] as const;

interface Created {
  ref: string;
  name: string;
  trackingUrl: string;
  subId: string;
  plan: string;
  brand: string;
}

export function NewAffiliateDialog({
  open,
  onClose,
  onDone,
  brands,
  defaultBrandId,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
  brands: { id: string; name: string }[];
  defaultBrandId: string | null;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();

  const [name, setName] = useState('');
  /*
   * The brand is chosen LAZILY, not in the state initialiser.
   *
   * This dialog is mounted from the screen's first render — `open` only toggles
   * its visibility — and `brands` arrives from GET /api/brands a moment later.
   * A `useState(brands[0]?.id ?? '')` therefore froze the empty string in
   * before the list existed, and the form refused every submission with "Pick
   * the brand this affiliate sends traffic to" beside a select that was already
   * showing one. `null` means "the operator has not chosen", and the fallback
   * below is evaluated at render, when the brands are actually there.
   */
  const [chosenBrand, setChosenBrand] = useState<string | null>(null);
  const brandId = chosenBrand ?? defaultBrandId ?? brands[0]?.id ?? '';
  const [sourceKind, setSourceKind] = useState<string>(SOURCE_KINDS[0]);
  const [subId, setSubId] = useState('sub_home');
  const [channel, setChannel] = useState('Site homepage');
  const [plan, setPlan] = useState<PlanTermsState>(EMPTY_PLAN);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  /* What was created, kept so the tracking link can be SHOWN and copied. The
     link is the one thing the operator has to take away from this dialog, and
     it is minted server-side from the ref the sequence gave it. */
  const [created, setCreated] = useState<Created | null>(null);

  const close = () => {
    setName('');
    setChosenBrand(null);
    setSourceKind(SOURCE_KINDS[0]);
    setSubId('sub_home');
    setChannel('Site homepage');
    setPlan(EMPTY_PLAN);
    setFields({});
    setCreated(null);
    onClose();
  };

  const submit = async () => {
    const terms = planTermsToBody(plan);
    const problems: Record<string, string> = { ...terms.fields };
    if (name.trim().length < 2) problems['name'] = 'Give the affiliate a name.';
    if (brandId === '') problems['brandId'] = 'Pick the brand this affiliate sends traffic to.';
    if (!/^[a-z0-9_]{3,48}$/.test(subId.trim())) {
      problems['subId'] =
        'A sub-ID is 3–48 lowercase letters, digits and underscores — it goes in a URL.';
    }
    if (channel.trim().length < 2) problems['channel'] = 'Name the channel this link is for.';
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<Created>('/api/admin/affiliates', {
        name: name.trim(),
        brandId,
        sourceKind,
        subId: subId.trim(),
        channel: channel.trim(),
        ...terms.body,
      });
      setCreated(result);
      setFields({});
      toast(`${result.ref} filed — pending approval by a second admin.`);
      onDone();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: 'Could not file the affiliate',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was created. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setBusy(false);
    }
  };

  const copyLink = () => {
    if (created === null) return;
    navigator.clipboard
      .writeText(`https://${created.trackingUrl}`)
      .then(() => toast(`Tracking link copied · ${created.ref}`))
      .catch(() =>
        showError({
          title: 'Could not copy the tracking link',
          message: `The browser refused clipboard access. The link is https://${created.trackingUrl}`,
        }),
      );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      title={created === null ? 'New affiliate' : `${created.ref} filed`}
      actions={
        created === null ? (
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button variant="primary" busy={busy} onClick={() => void submit()}>
              File affiliate
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        )
      }
    >
      {created === null ? (
        <div className="stack">
          <p style={{ margin: 0 }}>
            A tracking link and sub-ID prefix are issued on creation; commission starts on
            approval. A new affiliate starts <strong>Pending</strong> with no signups, no FTDs and
            no commission, and is approved by a different admin from the one who files it.
          </p>

          <Field label="Affiliate name" error={fields['name']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Pinoy Slots Review"
              />
            )}
          </Field>

          <Field label="Brand" error={fields['brandId']}>
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={brandId}
                onChange={(e) => setChosenBrand(e.target.value)}
              >
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Traffic source" error={fields['sourceKind']}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={sourceKind}
                onChange={(e) => setSourceKind(e.target.value)}
              >
                {SOURCE_KINDS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="First sub-ID"
            error={fields['subId']}
            hint="Added to the end of the tracking link — this is how the affiliate tells its placements apart."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={subId}
                onChange={(e) => setSubId(e.target.value)}
                placeholder="sub_home"
              />
            )}
          </Field>

          <Field label="Channel this link is for" error={fields['channel']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="Site homepage"
              />
            )}
          </Field>

          <PlanFields state={plan} onChange={setPlan} fields={fields} />

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Attribution is last-click, within the window set by{' '}
              the attribution window. The plan set here is the one every player
              acquired under it keeps — changing it later affects future cohorts only.
            </div>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p style={{ margin: 0 }}>
            <strong>{created.name}</strong> is filed as {created.ref} on {created.brand}, pending
            approval. It is on the {created.plan} plan and will not earn until a second admin
            approves it.
          </p>

          <h4 className="panel-title" style={{ margin: 0 }}>
            Tracking link
          </h4>
          {/* Shown in full, because a half link in a campaign brief sends
              traffic nowhere. The domain comes from platform.brand.domain and
              the URL is assembled server-side, so what is copied here is what
              the click handler will actually see. */}
          <div className="notice notice-quiet">
            <Icon name="link-simple" />
            <div>
              <code>https://{created.trackingUrl}</code>
              <div className="text-subtle" style={{ fontSize: '11px' }}>
                sub-ID {created.subId}
              </div>
            </div>
          </div>
          <div className="row">
            <Button onClick={copyLink}>
              <Icon name="copy" />
              Copy tracking link
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
